import React, { useEffect, useState } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import {
  GiPlantWatering,
  GiFarmTractor,
  GiWheat,
  GiThreeLeaves,
  GiPlantRoots,
} from 'react-icons/gi';
import { FaLeaf } from 'react-icons/fa';
import Reveal from '../Reveal/Reveal';
import '../../styles/about.css';

const DECOR = [
  { Icon: FaLeaf, cls: 'a1' },
  { Icon: GiWheat, cls: 'a2' },
  { Icon: GiThreeLeaves, cls: 'a3' },
  { Icon: GiPlantRoots, cls: 'a4' },
];

const FEATURES = [
  { icon: <FiCheckCircle />, label: '100% Organic', color: '#4f7942' },
  { icon: <GiPlantWatering />, label: 'Chemical Free', color: '#2f8f9d' },
  { icon: <GiFarmTractor />, label: 'Traditional Way of Farming', color: '#c9862f' },
  { icon: <GiWheat />, label: 'Fresh Harvest', color: '#b7791f' },
];

/* Carousel images live in public/about/.
   Add more filenames here as you upload them (caro2.jpg, caro3.jpg …). */
const SLIDES = [
  `${process.env.PUBLIC_URL}/about/caro1.jpg`,
  `${process.env.PUBLIC_URL}/about/caro2.jpg`,
  `${process.env.PUBLIC_URL}/about/caro3.jpg`,
  `${process.env.PUBLIC_URL}/about/caro4.jpg`,
  `${process.env.PUBLIC_URL}/about/caro5.jpg`,
];

/* Shown only if an about image hasn't been uploaded yet */
const FALLBACKS = [
  'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=900&q=75',
  'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?auto=format&fit=crop&w=900&q=75',
  'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=900&q=75',
];

export default function About() {
  const [index, setIndex] = useState(0);
  const count = SLIDES.length;

  useEffect(() => {
    if (count < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), 3000);
    return () => clearInterval(id);
  }, [count]);

  return (
    <section id="about" className="section about">
      <div className="about__decor" aria-hidden="true">
        {DECOR.map(({ Icon, cls }) => (
          <Icon key={cls} className={`about__leaf ${cls}`} />
        ))}
      </div>

      <div className="container">
        <Reveal className="section-head">
          <h2 className="section-title">About Mahizh Farms</h2>
          <p className="section-subtitle">
            Healthy food begins with healthy soil.
          </p>
        </Reveal>

        <div className="about__grid">
          <Reveal className="about__media">
          <div className="about__carousel">
            <div
              className="about__track"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {SLIDES.map((src, i) => (
                <div className="about__slide" key={i}>
                  <img
                    src={src}
                    alt={`Mahizh farm ${i + 1}`}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACKS[i % FALLBACKS.length];
                    }}
                  />
                </div>
              ))}
            </div>

            {count > 1 && (
              <>
                <div className="about__dots">
                  {SLIDES.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`about__dot ${i === index ? 'is-active' : ''}`}
                      onClick={() => setIndex(i)}
                      aria-label={`Go to image ${i + 1}`}
                    />
                  ))}
                </div>
                </>
              )}
            </div>
          </Reveal>

          <div className="about__content">
            <Reveal delay={1}>
              <p>
                Mahizh Natural Farming believes that healthy food begins with
                healthy soil. We cultivate crops using traditional farming
                techniques without harmful chemicals.
              </p>
              <p>
                Every harvest reflects our commitment to sustainability,
                biodiversity, and the well-being of future generations —
                bringing the wisdom of traditional Tamil agriculture to your
                table.
              </p>
            </Reveal>

            <div className="about__features">
              {FEATURES.map((f, i) => (
                <Reveal
                  key={f.label}
                  delay={Math.min(i + 1, 5)}
                  className="about__feature"
                  style={{ '--feat-color': f.color }}
                >
                  <span className="about__feature-icon">{f.icon}</span>
                  <span>{f.label}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
